import type { DateTime } from "luxon";
import type { FacilityChoice, SpecialNurseConfig } from "../../../../contexts/AuthProvider";
import type { DepartmentWageConfig } from "../../../../utils/DepartmentWage.config";
import type {
	AdminRole,
	AutoApproveRule,
	BreakRulesConfig,
	DepartmentType,
	EmrIntegrationInput,
	HoursRateConfig,
	HppdTarget,
	HrisIntegrationInput,
	NurseRole,
	OvertimeConfig,
	RemoveUnionStaffWarning,
	StatsMode,
} from "../../../../utils/models";
import type { PendingJobPpdOverrides } from "./jobPpdOverrides";

export enum TrainingWageType {
	FlatRate = "FlatRate",
	Percentage = "Percentage",
}

/**
 * What a programmatic `submitDeptConfig` caller gets back.
 *
 * `saved` is false for every silent short-circuit (the Save button is disabled with a tooltip, so
 * returning early is invisible and correct *for that caller*) and for a handled failure. `uiErrorKey`
 * carries the backend's `UIVisibleError` message key when the refusal had one — the timezone lock on
 * `PUT /departments/{id}/config` answers 409 with `department.config.timezone.locked.has.shifts`, and
 * a caller that flattened that into "the save failed" would drop the only sentence that explains it.
 */
export type SubmitDeptConfigResult = {
	saved: boolean;
	uiErrorKey?: string;
};

export type GeneralFormData = {
	id?: string;
	displayName: string;
	hospital: string;
	/**
	 * The facility a department is created in. Read only by the create; an existing department's
	 * facility is not edited on this form, so it loads as `null` and is never sent on an update.
	 * `null` on a new department until Ops picks one, which blocks Save.
	 */
	facility: FacilityChoice | null;
	/** Backward-compat scalar — average of `targetHPPDPerDay`. Sent on save for pre-INH-3388 server-side compat. */
	targetHPPD: number;
	targetHPPDPerDay: HppdTarget;
	targetPPD: number;
	minValidHPPDPercent: number;
	maxValidHPPDPercent: number;
	week1FirstSunday: DateTime;
	totalBeds: number;
	timezone: string;
	roles: NurseRole[];
	defaultRole?: NurseRole;
	notifyBeforeShiftStartMintutes?: number;
	notifyBeforeShiftEndMintutes?: number;
	partialBlockShifts: Partial<Record<`${NurseRole}` | "default", number[]>>;
	/**
	 * In the DB the time is saved as floats.
	 * In `23.25` the "23" indicates the hours and the ".25" indicates the hour fraction (15 minutes)
	 */
	fullShiftTimes: Partial<Record<`${NurseRole}` | "default", [start: number, end: number][]>>;
	jobShiftTimes: Record<string, { partialBlockShifts: number[]; fullShiftTimes: [start: number, end: number][] }>;
	/**
	 * PPD-inclusion edits waiting on Save. Overrides live in a table of their own, so they are flushed
	 * by their own endpoint once the config PUT succeeds — never sent in the config payload.
	 */
	jobPpdOverrides: PendingJobPpdOverrides;
	masterScheduleWeeks: number;
	canViewNurses: boolean;
	/**
	 * Last day of schedule staff can see in the app. `null` = no horizon, staff see everything.
	 * Also written by the Staffing "Staff Schedule Visibility" popup and the auto-post job, so the
	 * config save only sends it when Ops actually changed it here (see `useUpdateGeneralForm`).
	 */
	staffScheduleDisplayUntilDate: DateTime | null;
	hideOverstaffingWarning: boolean;
	countBedholdInCensus: boolean;
	/**
	 * Facility-local hours at which census is read from ADT; the day's census becomes their mean.
	 * `null` = off, and must stay `null` rather than `[]` — an empty array is rejected by the column
	 * and would otherwise read as "average over no readings".
	 */
	censusIntervals: number[] | null;
	/** Kept in the DB's polarity; the Integrations tab renders the inverse ("separate ... shifts"). */
	combineConsecutiveAgencyShifts: boolean;
	agencyPostDelayDefaultHours: number;
	prnCycleDurationDays?: number;
	roleGroups: Record<string, string[]>;
	hrisIntegrations: HrisIntegrationInput[];
	emrIntegrations: EmrIntegrationInput[];
	statsMode: StatsMode;
	removeUnionStaffWarning: RemoveUnionStaffWarning;
	departmentWageConfig: DepartmentWageConfig;
	specialNurses: Record<string, SpecialNurseConfig>;
	holidayBoundaries?: {
		startHour: number;
		endHour: number;
	} | null;
	dayShiftBoundaries?: {
		startHour: number;
		endHour: number;
	} | null;
	weekendBoundaries?: {
		startDay: number;
		startHour: number;
		endDay: number;
		endHour: number;
	} | null;
	weekStartDay?: number;
	units?: string[];
	floors?: string[];
	/** Call-off window bounds, in hours. `null` when unset — the backend then applies 24h before / 1h after. */
	callOffBypassHoursBeforeShiftBedside: number | null;
	callOffBypassHoursAfterStartBedside: number | null;
	callOffBypassHoursBeforeShiftAdmin: number | null;
	callOffBypassHoursAfterStartAdmin: number | null;
	/**
	 * Minutes before shift start by which all call-off open-shift messages should be sent. `null` =
	 * message everyone right away. Clearing the input must send `null` (not `undefined`) so the config
	 * save actually resets the column — an omitted key is preserved by the backend's exclude_unset dump.
	 */
	callOffMessageLeadTimeMinutes?: number | null;
	/**
	 * Days before a shift when the pickup agent may notify staff whose pickup would create overtime.
	 * `null` preserves the existing behavior; `0` permits immediate notification.
	 */
	overtimeEligibilityReleaseDays: number | null;
	/**
	 * Hours before a shift when the pickup agent may notify staff whose pickup would create overtime —
	 * the sub-day-granularity companion to `overtimeEligibilityReleaseDays`. The two are mutually
	 * exclusive in form state (at most one is non-null, which encodes the chosen Days/Hours unit); the
	 * unselected one is sent as `null` so the backend never has to guess which unit the user meant.
	 * `null` when the unit is Days or the feature is off.
	 */
	overtimeEligibilityReleaseHours: number | null;
	/**
	 * Clipboard Facility ID — the workplace `userId` from Clipboard Health. `null` = explicitly
	 * cleared; must send `null` (not `undefined`) so the backend's exclude_unset dump writes the
	 * column. An omitted key is preserved server-side (same pattern as callOffMessageLeadTimeMinutes).
	 */
	clipboardFacilityId?: string | null;
	nursaFacilityId?: string | null;
	hoursRateConfig?: HoursRateConfig[];
	dayStart?: number;
	/**
	 * DAT-527: the payroll/OT clock, independent of `dayStart` (which stays the presentation day
	 * boundary). Float hours like `dayStart` — 23.5 = 23:30.
	 *
	 * `null` means "not decoupled": overtime keeps using `dayStart`. Must be sent as null rather
	 * than a copy of `dayStart` — a stored value makes the column non-NULL, and a later write that
	 * changes `dayStart` alone would then silently switch overtime onto the payroll clock.
	 */
	payrollStartTime?: number | null;
	overtimeConfig?: OvertimeConfig[];
	breakRulesConfig?: BreakRulesConfig[];
	autoApproveRules?: AutoApproveRule[];
	hrisPositions?: string[];
	lateArrivalMinutes: number;
	earlyDepartureMinutes: number;
	shiftDeltaMinutes: number;
	/** Day-start grace for scheduled shifts. No UI control yet — round-trips so support can set it. */
	scheduledShiftDeltaMinutes: number;
	shiftDeltaThresholdHours: number;
	departmentType?: DepartmentType;
	jobIds: string[];
	longShiftDuration: number;
	nurseInactivityAlertDays: number | null;
	nurseInactiveAfterDays: number | null;
	departmentDataHistoryWeeks: number;
	autoCreateScheduleWeeksAhead: number;
};

// Re-exported so this module is the only import site for the admin row shape.
export type { AdminRole };

/** The 12 permissions a role can grant, named as the backend stores them. */
export type AdminPermission =
	| "is_readonly"
	| "hide_wage"
	| "disable_edit_master_schedule"
	| "modify_user"
	| "call_off_admin"
	| "receive_daily_admin_notifications"
	| "receive_action_required_admin_notifications"
	| "send_daily_pickup_summary"
	| "receive_performance_report"
	| "receive_daily_excel_report"
	| "receive_sms_response_notifications"
	| "receive_nurse_inactivity_notifications";

/**
 * What `GET /staffing/admin-role-defaults` returns. Empty or missing means the role grants nothing.
 * `string` is allowed too, so a permission added in SQL does not break parsing — it is just ignored.
 */
export type AdminRoleDefaults = Partial<Record<AdminRole, (AdminPermission | string)[]>>;

export type AdminUser = {
	id?: string;
	/** Roles in this department. Always a list — `[]` means no role, never null. */
	adminRoles: AdminRole[];
	firstName: string;
	phoneNumber?: string;
	/** Empty string for admins with no email. Never null. */
	username: string;
	requiresMFA: boolean;
	isReadonly: boolean;
	hideWage: boolean;
	disableEditMasterSchedule: boolean;
	callOffAdmin: boolean;
	support: boolean;
	modifyUser: boolean;
	receiveDailyAdminNotifications: boolean;
	receiveActionRequiredAdminNotifications: boolean;
	sendDailyActualShiftsCheck: boolean;
	sendDailyPickupSummary: boolean;
	receivePerformanceReport: boolean;
	receiveDailyExcelReport: boolean;
	receiveSmsResponseNotifications: boolean;
	receiveNurseInactivityNotifications: boolean;
	/** Last sign-in as an ISO timestamp, null if never. Read-only: written by the login flow. */
	lastLoginTime?: string | null;
	rowDisabled?: boolean;
};

/** An admin row without the roles — the permissions save must not carry them. */
export type AdminUserPermissions = Omit<AdminUser, "adminRoles">;

export type UpdateAdminUsersResponse = {
	users: AdminUser[];
	failedWelcomeEmails: string[];
};

/** Why a feature flag's value on this page may not be what another user in the same department sees.
 * Mirrors the backend `FlagTargetingCaveat`. */
export type FlagTargetingCaveatKind = "non_department_attribute" | "segment" | "individual_targets" | "prerequisite" | "percentage_rollout";

export type FlagTargetingCaveat = {
	kind: FlagTargetingCaveatKind;
	/** The specifics behind the caveat: attribute names, segment keys, prerequisite flag keys, or a
	 * single stringified count for individually targeted contexts. Empty for a percentage rollout. */
	details: string[];
};

/** Where a flag's "nothing matched" value comes from. Mirrors the backend `FlagDefaultSource`. */
export type FlagDefaultSource = "off_variation" | "fallthrough" | "percentage_rollout" | "code_default";

/** One row of the support-only feature flag inspector. Field names are the API's own snake_case —
 * the backend serializes Pydantic field names verbatim. `value` and `default_value` are whatever the
 * flag's variations hold: boolean for most flags, numbers or JSON objects for multivariate ones. */
export type FlagAdminMetadata = {
	value: unknown;
	/** Which part of the targeting tree produced `value`: RULE_MATCH, TARGET_MATCH, FALLTHROUGH, OFF,
	 * PREREQUISITE_FAILED or ERROR. */
	value_reason: string | null;
	targeting_on: boolean;
	default_value: unknown;
	default_source: FlagDefaultSource;
	caveats: FlagTargetingCaveat[];
	/** Null when the deployment's LaunchDarkly environment could not be identified — a link to the
	 * wrong environment would show targeting that does not explain the row. */
	ld_link: string | null;
};
